import _ from "lodash";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from "react";
import classes from "./CommentItemV2.scss";
import SingleUserProfile from "AppComponents/Post/components/SingleUserProfile";
import { getTimeSince, onEnterOrSpaceKeyDown } from "Utils";
import I18nHOC from "UIComponents/I18nHOC";
import TextMoreLess from "UIComponents/TextMoreLess";
import { compose } from "lib/apolloClient";
import classNames from "classnames";
import {
  Dropdown,
  DropdownMenu,
  IconButton,
  useDSContext,
} from "@toddle-edu/ds-web";
import { DotsVerticalOutlined } from "@toddle-edu/ds-icons";
import AttachmentList from "Attachment/AttachmentList";
import { useIsIntersecting } from "./useIsIntersecting";
import {
  getFormattedUserNameWithoutPronoun,
  isPortfolioParityEnabledAndIsJournalRoute,
} from "Post/utils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
const BLANK_OBJECT = {};

const styles = {
  commentTextContainer: {
    lineHeight: "2.4rem",
  },
  commentUserName: {
    fontWeight: "600",
  },
  subtextStyle: {
    fontSize: "1rem",
  },
  avatarStyles: {
    marginRight: "8px",
  },
};

const showMoreButtonProps = {
  dsVersion: "2.0",
  type: "inline",
  size: "medium",
};

const getCommentText = ({ comment, createdByUserName, isDSV2 }) => {
  return (
    <div
      data-test-id="journal-feed-post-commentitem"
      className={isDSV2 ? "text-body" : "text-body-1"}
      style={isDSV2 ? {} : styles.commentTextContainer}
    >
      {isDSV2 ? (
        <span className="text-label">{`${createdByUserName} `}</span>
      ) : (
        <b style={styles.commentUserName}>{`${createdByUserName} `}</b>
      )}
      <TextMoreLess
        value={_.get(comment, "label", "")}
        minHeight={60}
        containerDivStyle={{ display: "inline", wordBreak: "break-word" }}
        buttonProps={showMoreButtonProps}
      />
    </div>
  );
};

const getMenuOptions = (t, canEdit, canDelete) => {
  const menuItems = [];
  if (canEdit)
    menuItems.push({
      label: t("common:edit"),
      key: "EDIT_COMMENT",
    });
  if (canDelete)
    menuItems.push({
      label: t("common:delete"),
      key: "DELETE_COMMENT",
      isDestructive: true,
    });
  return menuItems;
};
const CommentItemV2 = props => {
  const {
    comment,
    canEdit,
    canDelete,
    onEditComment,
    onDeleteComment,
    t,
    showCommentBadge,
    updateCommentReadCount,
    excludePronounFromAvatar = false,
    isProjectPostCard,
  } = props;

  const { dsVersion } = useDSContext();
  const isDSV2 = dsVersion === "2.0";

  const isCommentRead = _.get(comment, "isCommentRead", true);

  const onMenuItemClick = item => {
    switch (item?.key) {
      case "EDIT_COMMENT":
        onEditComment(comment);
        break;
      case "DELETE_COMMENT":
        onDeleteComment(comment);
        break;
    }
  };

  const commentId = _.get(comment, "id", null);

  const commentRef = useRef(null);
  const isFirstIntersection = useRef(true);
  const isIntersecting = useIsIntersecting(commentRef, {
    threshold: 1,
    skip: !_.isFunction(updateCommentReadCount),
  });

  useEffect(() => {
    if (
      !isIntersecting ||
      !isFirstIntersection.current ||
      !_.isFunction(updateCommentReadCount)
    )
      return;

    isFirstIntersection.current = false;
    updateCommentReadCount();
  }, [isIntersecting]);

  const { createdBy } = comment || {};
  const createdByUserName = getFormattedUserName({ user: createdBy });
  const createdByUserNameExcludingPronoun = getFormattedUserNameWithoutPronoun({
    user: createdBy,
  });

  const timeSinceClasses = classNames({
    [classes.commentTimeSubtext]: !isDSV2,
    [classes.commentTimeSubtextV2]: isDSV2,
    "text-body-s": true,
  });

  const userContainerClasses = classNames({
    [classes.userContainer]: true,
    [classes.userContainerV2]: isDSV2,
  });

  const dotClasses = classNames({
    [classes.dot]: true,
    [classes.dotV2]: isDSV2,
  });

  const isJournalRoute = isPortfolioParityEnabledAndIsJournalRoute();

  return !_.isNil(comment) ? (
    <div
      ref={commentRef}
      className={classes.commentViewContainer}
      id={commentId}
      style={{
        margin: isJournalRoute ? "6px 0px" : "",
      }}
    >
      <div
        className={
          isJournalRoute
            ? classes.commentWithVoiceContainer
            : classes.commentWithVoiceContainerV2
        }
      >
        <div className={userContainerClasses}>
          {!isCommentRead && showCommentBadge ? (
            <div className={dotClasses} />
          ) : null}

          <SingleUserProfile
            username={
              excludePronounFromAvatar
                ? createdByUserNameExcludingPronoun
                : createdByUserName
            }
            id={_.get(comment, "createdBy.id", "")}
            profileImageSrc={_.get(comment, "createdBy.profileImage", "")}
            avatarSize={"small"}
            customUserText={getCommentText({
              comment,
              createdByUserName,
              isDSV2,
            })}
            subtextStyle={styles.subtextStyle}
            avatarStyles={styles.avatarStyles}
            isCommentItemUser={true}
            isProjectPostCard={isProjectPostCard}
          />
        </div>

        <AttachmentList
          value={
            _.isEmpty(_.get(comment, "item.attachments[0]", BLANK_OBJECT))
              ? []
              : [{ ..._.get(comment, "item.attachments[0]", BLANK_OBJECT) }]
          }
          name={"attachments"}
          cardType={"horizontal"}
          showCreateDropdown={false}
          clubbingCards={1}
          showSingleCardTheme={true}
          cardStyle={{ height: 80 }}
          attachmentContainerStyle={{
            gridTemplateColumns: "repeat(1,1fr)",
            gridGap: "12px",
            marginTop: "8px",
            marginBottom: "8px",
          }}
          containerStyle={{ padding: "0 0 0 20px" }}
          isRightPaneChat={false}
          showLocalAttachmentsInViewMode={false}
          mode={"view"}
          showAudioDownloadButton={false}
        />
        <div className={timeSinceClasses}>
          {getTimeSince(_.get(comment, "createdAt"), t)}
        </div>
      </div>

      {(canEdit || canDelete) && !_.includes(commentId, "NEW_") && (
        <div
          className={classes.dropdownOptionsContainer}
          onKeyDown={e => e.stopPropagation()}
        >
          <Dropdown
            overlay={
              <DropdownMenu
                options={getMenuOptions(t, canEdit, canDelete)}
                onClick={onMenuItemClick}
                tabIndex={0}
                onKeyDown={e => onEnterOrSpaceKeyDown(e, onMenuItemClick)}
                testId="journal-feed-post-editcomment"
              />
            }
          >
            <IconButton
              dsVersion="2.0"
              size="medium"
              icon={<DotsVerticalOutlined />}
              _legacy_size={"small"}
              _legacy_variant="plain"
              type={"plain"}
              variant="neutral"
              testId="journal-feed-post-editcomment"
            />
          </Dropdown>
        </div>
      )}
    </div>
  ) : null;
};

export default compose(I18nHOC({ resource: ["common"] }))(CommentItemV2);
